import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Modal,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";

import axios from "axios";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ReportPdfColorModal from "../Report/ReportPdfColorModal";

import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { usePdfSettings, DEFAULT_PDF_COLORS } from "../../layouts/PdfSettingsContext";
import { useBranch } from "../../layouts/BranchContext";

const BASE_URL = "https://wims.revisetoexcel.com/api";

// =========================================================
// API
// =========================================================

const REPORT_APIS = {
  overall: "/library/expense/report/",
  branch: "/library/expense/report/branch/",
  category: "/library/expense/report/category/",
  monthly: "/library/expense/report/monthly/",
  payment: "/library/expense/report/payment-mode/",
  staff: "/library/expense/report/staff/",
};

// =========================================================
// TABS
// =========================================================

const TAB_CONFIG = [
  { key: "overall", label: "Overall" },
  { key: "branch", label: "Branch-wise" },
  { key: "category", label: "Category-wise" },
  { key: "monthly", label: "Monthly" },
  { key: "payment", label: "Payment Mode" },
  { key: "staff", label: "Staff-wise" },
];

// =========================================================
// REPORT-WISE PDF CONFIG
// =========================================================
const REPORT_PDF_COLUMNS = {
  overall: ["date", "category", "amount", "payment_mode", "status", "entered_by", "approved_by"],
  branch: ["branch", "total_amount", "expense_count"],
  category: ["category", "total_amount", "expense_count"],
  monthly: ["month", "year", "total_amount", "expense_count"],
  payment: ["payment_mode", "total_amount", "expense_count"],
  staff: ["entered_by", "total_amount", "expense_count"],
};

const REPORT_PDF_WIDTHS = {
  overall: { date: 24, category: 34, amount: 24, payment_mode: 28, status: 24, entered_by: 34, approved_by: 34 },
  branch: { branch: 55, total_amount: 45, expense_count: 35 },
  category: { category: 60, total_amount: 45, expense_count: 35 },
  monthly: { month: 35, year: 25, total_amount: 45, expense_count: 35 },
  payment: { payment_mode: 55, total_amount: 45, expense_count: 35 },
  staff: { entered_by: 60, total_amount: 45, expense_count: 35 },
};


function ExpenseReport() {
  const [activeTab, setActiveTab] = useState("overall");

  // =========================================================
  // PAGINATION
  // =========================================================

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // =========================================================
  // GLOBAL BRANCH CONTEXT
  // =========================================================
  const branchContext = useBranch();
  const {
    selectedBranchId,
    branchList: globalBranchList = [],
    organizationId: globalOrganizationId,
    setSelectedBranchId,
  } = branchContext || {};

  // =========================================================
  // FILTERS
  // =========================================================

  const [filters, setFilters] = useState({
    organization: "",
    branch: "",
    category: "",
    status: "",
    payment_mode: "",
    entered_by: "",
    approved_by: "",
    date_from: (() => {
      const date = new Date();
      date.setMonth(date.getMonth() - 1);
      return date.toISOString().split("T")[0];
    })(),
    date_to: new Date().toISOString().split("T")[0],
    year: new Date().getFullYear(),
  });

  // =========================================================
  // REPORT STATES
  // =========================================================

  const [reportData, setReportData] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset pagination whenever report/tab/filter results change.
  useEffect(() => {
    setPage(0);
  }, [activeTab, reportData.length]);
  const [error, setError] = useState("");

  // =========================================================
  // DROPDOWN STATES
  // =========================================================

  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);

  const [organizationName, setOrganizationName] = useState("");

  // =========================================================
  // DYNAMIC BRANCH PDF DATA
  // =========================================================

  const [branchDetails, setBranchDetails] = useState(null);
  const [branchName, setBranchName] = useState("");
  const [branchLogo, setBranchLogo] = useState("");

  // =========================================================
  // PDF SETTINGS
  // =========================================================

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  const [tempPdfThemeColor, setTempPdfThemeColor] =
    useState(
      pdfThemeColor || DEFAULT_PDF_COLORS.primary
    );

  const [tempPdfTextColor, setTempPdfTextColor] =
    useState(
      pdfTextColor || DEFAULT_PDF_COLORS.bodyText
    );

  const [showColorPickerModal, setShowColorPickerModal] =
    useState(false);

  const [showFilterModal, setShowFilterModal] =
    useState(false);

  // =========================================================
  // GET CURRENT USER
  // =========================================================

  const getCurrentUser = () => {
    try {
      const sessionUser = sessionStorage.getItem("user");
      const localUser = localStorage.getItem("user");

      const user =
        (sessionUser && JSON.parse(sessionUser)) ||
        (localUser && JSON.parse(localUser));

      return user || {};
    } catch (error) {
      console.error("User parse error:", error);
      return {};
    }
  };

  // =========================================================
  // GET ACCESS TOKEN
  // =========================================================

  const getAccessToken = () => {
    const possibleTokens = [
      sessionStorage.getItem("access_token"),
      localStorage.getItem("access_token"),
      sessionStorage.getItem("accessToken"),
      localStorage.getItem("accessToken"),
      sessionStorage.getItem("token"),
      localStorage.getItem("token"),
    ];

    const token = possibleTokens.find(
      (item) => item && item.trim()
    );

    if (!token) {
      return "";
    }

    if (token.startsWith("Bearer ")) {
      return token;
    }

    return `Bearer ${token}`;
  };

  // =========================================================
  // AUTH HEADERS
  // =========================================================

  const getAuthHeaders = () => {
    const authorization = getAccessToken();

    if (!authorization) {
      console.warn("No access token found.");
    }

    return {
      Authorization: authorization,
    };
  };

  // =========================================================
  // INITIAL USER DATA

  useEffect(() => {
    const user = getCurrentUser();
    console.log("Logged-in user:", user);

    const resolvedOrganizationId =
      globalOrganizationId ||
      user.organization_id ||
      (typeof user.organization === "object" ? user.organization?.id : user.organization) ||
      "";

    const resolvedBranchId =
      selectedBranchId ||
      user.branch_id ||
      (typeof user.branch === "object" ? user.branch?.id : user.branch) ||
      "";

    const orgName =
      user.organization_name ||
      user.organizationName ||
      (typeof user.organization === "object" ? user.organization?.name : "") ||
      "";

    setOrganizationName(orgName);
    setFilters((prev) => ({
      ...prev,
      organization: resolvedOrganizationId,
      branch: resolvedBranchId,
    }));
  }, [globalOrganizationId, selectedBranchId]);

  // Global Branch Context is the source of truth for this report.
  useEffect(() => {
    if (!selectedBranchId) return;
    setFilters((prev) => ({
      ...prev,
      organization: globalOrganizationId || prev.organization,
      branch: selectedBranchId,
      category: "",
      entered_by: "",
      approved_by: "",
    }));
  }, [selectedBranchId, globalOrganizationId]);

  // =========================================================
  // FETCH ORGANIZATIONS
  // =========================================================

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/master/organization/`,
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(
          "Organization API response:",
          response.data
        );

        const data = response.data;

        if (Array.isArray(data)) {
          setOrganizations(data);
        } else if (Array.isArray(data?.results)) {
          setOrganizations(data.results);
        } else if (Array.isArray(data?.data)) {
          setOrganizations(data.data);
        } else {
          setOrganizations([]);
        }
      } catch (error) {
        console.error(
          "Organization API error:",
          error.response?.data || error
        );

        setOrganizations([]);
      }
    };

    fetchOrganizations();
  }, []);

  // =========================================================
  // FETCH BRANCHES
  // ONLY SELECTED ORGANIZATION BRANCHES
  // =========================================================

  useEffect(() => {
    const organizationId = filters.organization;

    if (!organizationId) {
      setBranches([]);
      return;
    }

    const fetchBranches = async () => {
      try {
        console.log(
          "Fetching branches for organization:",
          organizationId
        );

        const response = await axios.get(
          `${BASE_URL}/master/organization/${organizationId}/branches/`,
          {
            params: {
              organization_id: organizationId,
            },
            headers: getAuthHeaders(),
          }
        );

        console.log(
          "Branch API response:",
          response.data
        );

        const data = response.data;

        let branchData = [];

        if (Array.isArray(data)) {
          branchData = data;
        } else if (Array.isArray(data?.results)) {
          branchData = data.results;
        } else if (Array.isArray(data?.data)) {
          branchData = data.data;
        }

        // =====================================================
        // IMPORTANT:
        // Endpoint is already organization scoped.
        // If branch object contains organization info,
        // verify it also belongs to selected organization.
        // =====================================================

        const onlySelectedOrganizationBranches =
          branchData.filter((branch) => {
            // Ignore inactive branches
            if (branch?.is_active === false) {
              return false;
            }

            const branchOrganizationId =
              branch?.organization?.id ??
              branch?.organization_id ??
              branch?.organizationId ??
              null;

            // If API doesn't return organization metadata,
            // trust the organization-scoped endpoint.
            if (
              branchOrganizationId === null ||
              branchOrganizationId === undefined
            ) {
              return true;
            }

            return (
              String(branchOrganizationId) ===
              String(organizationId)
            );
          });

        console.log(
          "Filtered branches for selected organization:",
          onlySelectedOrganizationBranches
        );

        setBranches(
          globalBranchList.length
            ? globalBranchList.filter((branch) => {
                if (branch?.is_active === false) return false;
                const branchOrg =
                  branch?.organization?.id ??
                  branch?.organization_id ??
                  branch?.organizationId ??
                  null;
                return branchOrg == null || String(branchOrg) === String(organizationId);
              })
            : onlySelectedOrganizationBranches
        );

        // =====================================================
        // If currently selected branch does not belong to
        // selected organization, clear it.
        // =====================================================

        if (
          filters.branch &&
          !onlySelectedOrganizationBranches.some(
            (branch) =>
              String(branch.id) ===
              String(filters.branch)
          )
        ) {
          setFilters((prev) => ({
            ...prev,
            branch: "",
            category: "",
            entered_by: "",
            approved_by: "",
          }));

          setBranchDetails(null);
          setBranchLogo("");
          setBranchName("");
        }
      } catch (error) {
        console.error(
          "Branch API error:",
          error.response?.data || error
        );

        setBranches([]);
      }
    };

    fetchBranches();
  }, [filters.organization, globalBranchList]);

  // =========================================================
  // GET ABSOLUTE IMAGE URL
  //
  // IMPORTANT:
  // /media/... MUST become
  // https://wims.revisetoexcel.com/media/...
  //
  // NOT:
  // https://wims.revisetoexcel.com/api/media/...
  // =========================================================

  const getAbsoluteImageUrl = (value) => {
    if (!value) {
      return "";
    }

    if (typeof value !== "string") {
      return "";
    }

    const imageValue = value.trim();

    if (!imageValue) {
      return "";
    }

    // Data image
    if (
      imageValue.startsWith("data:image/")
    ) {
      return imageValue;
    }

    // Absolute HTTP/HTTPS URL
    if (
      imageValue.startsWith("http://") ||
      imageValue.startsWith("https://")
    ) {
      return imageValue;
    }

    // Protocol relative URL
    if (imageValue.startsWith("//")) {
      return `https:${imageValue}`;
    }

    // API origin WITHOUT /api
    const API_ORIGIN = BASE_URL.replace(
      /\/api\/?$/,
      ""
    );

    // /media/logo.png
    if (imageValue.startsWith("/")) {
      return `${API_ORIGIN}${imageValue}`;
    }

    // media/logo.png
    return `${API_ORIGIN}/${imageValue}`;
  };

  // =========================================================
  // SVG / IMAGE DATA URL -> PNG DATA URL
  // jsPDF reliably handles PNG/JPG.
  // =========================================================

  const dataUrlToPng = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        try {
          const canvas =
            document.createElement("canvas");

          canvas.width =
            img.naturalWidth ||
            img.width ||
            300;

          canvas.height =
            img.naturalHeight ||
            img.height ||
            100;

          const context =
            canvas.getContext("2d");

          if (!context) {
            reject(
              new Error(
                "Canvas context unavailable"
              )
            );
            return;
          }

          context.drawImage(
            img,
            0,
            0,
            canvas.width,
            canvas.height
          );

          resolve(
            canvas.toDataURL("image/png")
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = dataUrl;
    });
  };

  // =========================================================
  // FETCH IMAGE AND CONVERT TO DATA URL
  // =========================================================

  const imageUrlToDataUrl = async (
    imageUrl
  ) => {
    if (!imageUrl) {
      return "";
    }

    try {
      // Already data URL
      if (
        imageUrl.startsWith("data:image/")
      ) {
        if (
          imageUrl.startsWith(
            "data:image/svg+xml"
          )
        ) {
          return await dataUrlToPng(
            imageUrl
          );
        }

        return imageUrl;
      }

      const absoluteUrl =
        getAbsoluteImageUrl(imageUrl);

      if (!absoluteUrl) {
        return "";
      }

      console.log(
        "Fetching branch logo:",
        absoluteUrl
      );

      const response = await axios.get(
        absoluteUrl,
        {
          responseType: "blob",
          headers: getAuthHeaders(),
        }
      );

      const blob = response.data;

      const dataUrl =
        await new Promise(
          (resolve, reject) => {
            const reader =
              new FileReader();

            reader.onloadend = () => {
              resolve(
                reader.result
              );
            };

            reader.onerror = reject;

            reader.readAsDataURL(blob);
          }
        );

      // SVG -> PNG
      if (
        typeof dataUrl === "string" &&
        dataUrl.startsWith(
          "data:image/svg+xml"
        )
      ) {
        return await dataUrlToPng(
          dataUrl
        );
      }

      // PNG/JPG/JPEG/WEBP etc.
      return dataUrl;
    } catch (error) {
      console.error(
        "Branch logo conversion error:",
        error
      );

      return "";
    }
  };

  // =========================================================
  // FETCH SELECTED BRANCH DETAILS
  //
  // /master/branch/{branchId}/
  // =========================================================

  useEffect(() => {
    const branchId = filters.branch;

    if (!branchId) {
      setBranchDetails(null);
      setBranchLogo("");
      setBranchName("");
      return;
    }

    const fetchBranchDetails = async () => {
      try {
        console.log(
          "Fetching selected branch details:",
          branchId
        );

        const response = await axios.get(
          `${BASE_URL}/master/branch/${branchId}/`,
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(
          "Branch details response:",
          response.data
        );

        const data = response?.data || {};

        const dynamicBranchName =
          data?.name ||
          data?.branch_name ||
          data?.branchName ||
          "";

        const image =
          data?.image ||
          data?.logo ||
          data?.branch_logo ||
          data?.branchLogo ||
          "";

        const absoluteImageUrl =
          getAbsoluteImageUrl(image);

        setBranchDetails(data);
        setBranchName(
          dynamicBranchName
        );
        setBranchLogo(
          absoluteImageUrl
        );
      } catch (error) {
        console.error(
          "Branch details API error:",
          error.response?.data || error
        );

        setBranchDetails(null);
        setBranchLogo("");
        setBranchName("");
      }
    };

    fetchBranchDetails();
  }, [filters.branch]);

  // =========================================================
  // FETCH CATEGORIES
  // =========================================================

  useEffect(() => {
    const organizationId =
      filters.organization;

    const branchId = filters.branch;

    if (
      !organizationId ||
      !branchId
    ) {
      setCategories([]);
      return;
    }

    const fetchCategories =
      async () => {
        try {
          console.log(
            "Fetching categories:",
            {
              organization:
                organizationId,
              branch: branchId,
            }
          );

          const response =
            await axios.get(
              `${BASE_URL}/library/expense-category/dropdown/`,
              {
                params: {
                  organization:
                    organizationId,
                  branch: branchId,
                  is_active: true,
                },
                headers:
                  getAuthHeaders(),
              }
            );

          console.log(
            "Category API response:",
            response.data
          );

          const data =
            response.data;

          if (Array.isArray(data)) {
            setCategories(data);
          } else if (
            Array.isArray(
              data?.results
            )
          ) {
            setCategories(
              data.results
            );
          } else if (
            Array.isArray(
              data?.data
            )
          ) {
            setCategories(
              data.data
            );
          } else {
            setCategories([]);
          }
        } catch (error) {
          console.error(
            "Category API error:",
            error.response?.data ||
              error
          );

          setCategories([]);
        }
      };

    fetchCategories();
  }, [
    filters.organization,
    filters.branch,
  ]);

  // =========================================================
  // FETCH STAFF / EMPLOYEES
  // =========================================================

  useEffect(() => {
    const organizationId =
      filters.organization;

    const branchId =
      filters.branch;

    if (
      !organizationId ||
      !branchId
    ) {
      setStaff([]);
      return;
    }

    const fetchStaff =
      async () => {
        try {
          console.log(
            "Fetching employees:",
            {
              organization_id:
                organizationId,
              branch_id:
                branchId,
            }
          );

          const response =
            await axios.get(
              `${BASE_URL}/staff/employees/filter/`,
              {
                params: {
                  organization_id:
                    organizationId,
                  branch_id:
                    branchId,
                },
                headers:
                  getAuthHeaders(),
              }
            );

          console.log(
            "Employee API response:",
            response.data
          );

          const data =
            response.data;

          if (Array.isArray(data)) {
            setStaff(data);
          } else if (
            Array.isArray(
              data?.results
            )
          ) {
            setStaff(
              data.results
            );
          } else if (
            Array.isArray(
              data?.data
            )
          ) {
            setStaff(
              data.data
            );
          } else {
            setStaff([]);
          }
        } catch (error) {
          console.error(
            "Employee API error:",
            error.response?.data ||
              error
          );

          setStaff([]);
        }
      };

    fetchStaff();
  }, [
    filters.organization,
    filters.branch,
  ]);

  // =========================================================
  // PDF COLOR MODAL
  // =========================================================

  useEffect(() => {
    if (showColorPickerModal) {
      setTempPdfThemeColor(
        pdfThemeColor || DEFAULT_PDF_COLORS.primary
      );

      setTempPdfTextColor(
        pdfTextColor || DEFAULT_PDF_COLORS.bodyText
      );
    }
  }, [
    showColorPickerModal,
    pdfThemeColor,
    pdfTextColor,
  ]);

  // =========================================================
  // FILTER CHANGE
  // =========================================================

  const handleFilterChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    if (name === "branch") {
      setFilters((prev) => ({
        ...prev,
        branch: value,
        category: "",
        entered_by: "",
        approved_by: "",
      }));

      return;
    }

    if (name === "organization") {
      setFilters((prev) => ({
        ...prev,
        organization: value,
        branch: "",
        category: "",
        entered_by: "",
        approved_by: "",
      }));

      setBranchDetails(null);
      setBranchLogo("");
      setBranchName("");

      return;
    }

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // BUILD QUERY PARAMS
  // =========================================================

  const buildParams = () => {
    const params = {};

    Object.entries(filters).forEach(
      ([key, value]) => {
        if (
          value !== "" &&
          value !== null &&
          value !== undefined
        ) {
          params[key] = value;
        }
      }
    );

    // Always send the selected global branch as `branch`.
    const effectiveBranchId = selectedBranchId || filters.branch || "";
    if (effectiveBranchId) params.branch = effectiveBranchId;

    if (activeTab === "branch") {
      delete params.category;
      delete params.payment_mode;
      delete params.entered_by;
      delete params.approved_by;
      delete params.year;
    }

    if (activeTab === "category") {
      delete params.category;
      delete params.payment_mode;
      delete params.entered_by;
      delete params.approved_by;
      delete params.year;
    }

    if (activeTab === "monthly") {
      delete params.category;
      delete params.payment_mode;
      delete params.entered_by;
      delete params.approved_by;
      delete params.date_from;
      delete params.date_to;
    }

    if (activeTab === "payment") {
      delete params.category;
      delete params.payment_mode;
      delete params.entered_by;
      delete params.approved_by;
      delete params.year;
    }

    if (activeTab === "staff") {
      delete params.category;
      delete params.payment_mode;
      delete params.entered_by;
      delete params.approved_by;
      delete params.year;
    }

    if (activeTab === "overall") {
      delete params.year;
    }

    return params;
  };

  // =========================================================
  // FETCH REPORT
  // =========================================================

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        REPORT_APIS[activeTab];

      const params =
        buildParams();

      const response =
        await axios.get(
          `${BASE_URL}${endpoint}`,
          {
            params,
            headers:
              getAuthHeaders(),
          }
        );

      const data =
        response.data;

      const responseRows =
        Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];

      const responseSummary =
        data?.summary ||
        data?.report_summary ||
        data?.expense_summary ||
        data?.metadata?.summary ||
        null;

      setReportData(responseRows);
      setReportSummary(responseSummary);
      return responseRows;
    } catch (error) {
      console.error(
        "Expense report error:",
        error.response?.data ||
          error
      );

      setReportData([]);

      if (
        error.response?.status ===
        401
      ) {
        setError(
          "Authentication failed. Please login again."
        );
      } else if (
        error.response?.data
          ?.detail
      ) {
        setError(
          error.response.data
            .detail
        );
      } else if (
        error.response?.data
          ?.message
      ) {
        setError(
          error.response.data
            .message
        );
      } else {
        setError(
          "Failed to load expense report."
        );
      }

      return [];
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD REPORT
  //
  // Branch added here so changing branch uses the UPDATED
  // filter state instead of old state from setTimeout.
  // =========================================================

  useEffect(() => {
    if (filters.organization) {
      fetchReport();
    }
  }, [
    activeTab,
    filters.organization,
    filters.branch,
    selectedBranchId,
  ]);

  // =========================================================
  // RESET FILTERS
  // =========================================================

  const handleReset = () => {
    const user =
      getCurrentUser();

    const organizationId =
      globalOrganizationId ||
      user.organization_id ||
      user.organization ||
      "";

    const branchId =
      selectedBranchId ||
      user.branch_id ||
      user.branch ||
      "";

    setFilters({
      organization:
        organizationId,
      branch: branchId,
      category: "",
      status: "",
      payment_mode: "",
      entered_by: "",
      approved_by: "",
      date_from: (() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().split("T")[0];
      })(),
      date_to: new Date().toISOString().split("T")[0],
      year: new Date().getFullYear(),
    });

    setBranchDetails(null);
    setBranchLogo("");
    setBranchName("");
    setReportSummary(null);

    setError("");

    setTimeout(() => {
      fetchReport();
    }, 0);
  };

  // =========================================================
  // GET TABLE COLUMNS
  // =========================================================

  const getColumns = () => {
    if (!reportData.length) {
      return [];
    }

    return Object.keys(
      reportData[0]
    );
  };

  const columns =
    getColumns();

  // =========================================================
  // FORMAT COLUMN NAME
  // =========================================================

  const formatColumnName = (
    column
  ) => {
    return column
      .replace(/_/g, " ")
      .replace(
        /\b\w/g,
        (char) =>
          char.toUpperCase()
      );
  };

  // =========================================================
  // FORMAT VALUE
  // =========================================================

  const formatValue = (
    value
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    if (
      typeof value ===
      "object"
    ) {
      return JSON.stringify(
        value
      );
    }

    return String(value);
  };

  // =========================================================
  // REPORT TITLE
  // =========================================================

  const getReportTitle = () => {
    const tab =
      TAB_CONFIG.find(
        (item) =>
          item.key ===
          activeTab
      );

    return `${
      tab?.label || "Expense"
    } Expense Report`;
  };

  // =========================================================
  // HEX TO RGB
  // =========================================================

  const hexToRgb = (
    hex
  ) => {
    const cleanHex =
      hex.replace("#", "");

    const r = parseInt(
      cleanHex.substring(
        0,
        2
      ),
      16
    );

    const g = parseInt(
      cleanHex.substring(
        2,
        4
      ),
      16
    );

    const b = parseInt(
      cleanHex.substring(
        4,
        6
      ),
      16
    );

    return [r, g, b];
  };

  // =========================================================
  // GET STAFF ID
  // =========================================================

  const getStaffId = (
    person
  ) => {
    return (
      person?.id ??
      person?.employee_id ??
      person?.user_id ??
      person?.employee?.id ??
      ""
    );
  };

  // =========================================================
  // GET STAFF NAME
  // =========================================================

  const getStaffName = (
    person
  ) => {
    return (
      person?.name ||
      person?.full_name ||
      person?.employee_name ||
      person?.username ||
      person?.user?.name ||
      person?.user?.username ||
      `Employee ${getStaffId(
        person
      )}`
    );
  };

  // =========================================================
  // PDF HELPERS
  // =========================================================

  const getPdfColumns = () => {
    if (!reportData.length) return [];
    const preferred = REPORT_PDF_COLUMNS[activeTab] || [];
    const available = Object.keys(reportData[0] || {});
    const matched = preferred.filter((column) => available.includes(column));
    const remaining = available.filter((column) => !matched.includes(column));
    return matched.length ? [...matched, ...remaining] : available;
  };

  const getPdfColumnWidth = (column) =>
    REPORT_PDF_WIDTHS[activeTab]?.[column] || "auto";

  const getSummaryEntries = () => {
    if (!reportSummary) return [];
    if (Array.isArray(reportSummary)) {
      return reportSummary.map((item, index) => [
        item?.label || item?.name || `Summary ${index + 1}`,
        item?.value ?? item?.amount ?? item?.count ?? "-",
      ]);
    }
    if (typeof reportSummary === "object") {
      return Object.entries(reportSummary).filter(([, value]) =>
        value !== null && value !== undefined && typeof value !== "object"
      );
    }
    return [["Summary", reportSummary]];
  };

  const getGeneratedDate = () =>
    new Date().toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  // GENERATE PDF
  // =========================================================

  const exportExcel = () => {
    if (!reportData.length) {
      setError("No expense data available to export.");
      return;
    }

    try {
      const rows = reportData.map((row, index) => ({
        "S.No": index + 1,
        ...Object.entries(row || {}).reduce((acc, [key, value]) => {
          acc[key] = value ?? "-";
          return acc;
        }, {}),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = Object.keys(rows[0] || {}).map((key) => ({
        wch: Math.max(12, String(key).length + 4),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `${TAB_CONFIG.find((item) => item.key === activeTab)?.label || "Expense"} Report`
      );
      XLSX.writeFile(
        workbook,
        `${(TAB_CONFIG.find((item) => item.key === activeTab)?.label || "Expense").replace(/\s+/g, "_")}_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      console.error("Expense Excel export error:", error);
      setError("Failed to export Excel report.");
    }
  };

  const generatePDF = async (
    themeColor = pdfThemeColor || DEFAULT_PDF_COLORS.primary,
    textColor = pdfTextColor || DEFAULT_PDF_COLORS.bodyText
  ) => {
    if (!reportData.length) {
      alert(
        "No report data available to generate PDF."
      );

      return;
    }

    try {
      // =====================================================
      // MAKE SURE CURRENT BRANCH DETAILS ARE AVAILABLE
      // =====================================================

      let currentBranchName =
        branchName;

      let currentBranchLogo =
        branchLogo;

      const effectiveBranchId = selectedBranchId || filters.branch || "";

      if (effectiveBranchId) {
        try {
          const response =
            await axios.get(
              `${BASE_URL}/master/branch/${effectiveBranchId}/`,
              {
                headers:
                  getAuthHeaders(),
              }
            );

          const data =
            response?.data || {};

          currentBranchName =
            data?.name ||
            data?.branch_name ||
            data?.branchName ||
            currentBranchName ||
            "";

          const image =
            data?.image ||
            data?.logo ||
            data?.branch_logo ||
            data?.branchLogo ||
            "";

          currentBranchLogo =
            getAbsoluteImageUrl(
              image
            );

          setBranchDetails(data);
          setBranchName(
            currentBranchName
          );
          setBranchLogo(
            currentBranchLogo
          );
        } catch (branchError) {
          console.error(
            "Unable to refresh branch details for PDF:",
            branchError
          );
        }
      }

      // =====================================================
      // CONVERT LOGO FOR PDF
      // =====================================================

      let pdfLogo = "";

      if (currentBranchLogo) {
        pdfLogo =
          await imageUrlToDataUrl(
            currentBranchLogo
          );
      }

      // =====================================================
      // PDF
      // =====================================================

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth =
        doc.internal.pageSize.getWidth();

      const pageHeight =
        doc.internal.pageSize.getHeight();

      const margin = 10;

      const primaryColor =
        hexToRgb(themeColor);

      const textRgbColor =
        hexToRgb(textColor);

      const organizationTitle =
        organizationName ||
        "Organization";

      const finalBranchName =
        currentBranchName ||
        branches.find(
          (branch) =>
            String(branch.id) ===
            String(effectiveBranchId)
        )?.name ||
        branches.find(
          (branch) =>
            String(branch.id) ===
            String(effectiveBranchId)
        )?.branch_name ||
        branches.find(
          (branch) =>
            String(branch.id) ===
            String(effectiveBranchId)
        )?.branch ||
        "";

      // =====================================================
      // DRAW PDF HEADER
      //
      // This function is called for EVERY page.
      // =====================================================

      const drawPdfHeader = () => {
        const headerHeight = 36;

        // Header line
        doc.setDrawColor(
          ...primaryColor
        );

        doc.setLineWidth(0.5);

        doc.line(
          margin,
          headerHeight,
          pageWidth - margin,
          headerHeight
        );

        // ===================================================
        // DYNAMIC BRANCH LOGO
        // ===================================================

        if (pdfLogo) {
          try {
            doc.addImage(
              pdfLogo,
              "PNG",
              margin,
              4,
              32,
              24,
              undefined,
              "FAST"
            );
          } catch (logoError) {
            console.error(
              "PDF logo rendering error:",
              logoError
            );
          }
        }

        // ===================================================
        // ORGANIZATION
        // ===================================================

        doc.setFont(
          "times",
          "bold"
        );

        doc.setFontSize(16);

        doc.setTextColor(
          ...primaryColor
        );

        const organizationX =
          pdfLogo
            ? pageWidth / 2 + 5
            : pageWidth / 2;

        doc.text(
          organizationTitle,
          organizationX,
          11,
          {
            align: "center",
          }
        );

        // ===================================================
        // BRANCH NAME
        // ===================================================

        if (finalBranchName) {
          doc.setFont(
            "times",
            "normal"
          );

          doc.setFontSize(11);

          doc.setTextColor(
            ...textRgbColor
          );

          doc.text(
            finalBranchName,
            organizationX,
            18,
            {
              align: "center",
            }
          );
        }

        // ===================================================
        // REPORT TITLE
        // ===================================================

        doc.setFont(
          "times",
          "normal"
        );

        doc.setFontSize(12);

        doc.setTextColor(
          ...primaryColor
        );

        doc.text(
          getReportTitle(),
          organizationX,
          27,
          {
            align: "center",
          }
        );
      };

      // =====================================================
      // FIRST PAGE HEADER
      // =====================================================

      drawPdfHeader();

      // =====================================================
      // FILTER INFO
      // =====================================================

      let filterText = "";

      if (
        filters.date_from ||
        filters.date_to
      ) {
        filterText += `From: ${
          filters.date_from || "-"
        }  To: ${
          filters.date_to || "-"
        }`;
      }

      if (
        activeTab === "monthly"
      ) {
        if (filterText) {
          filterText += "    ";
        }

        filterText += `Year: ${
          filters.year
        }`;
      }

      if (filters.status) {
        if (filterText) {
          filterText += "    ";
        }

        filterText += `Status: ${
          filters.status
        }`;
      }

      let yOffset = 43;

      doc.setFontSize(8);
      doc.setFont("times", "normal");
      doc.setTextColor(...textRgbColor);
      doc.text(
        `Generated: ${getGeneratedDate()}`,
        pageWidth - margin,
        27,
        { align: "right" }
      );

      if (filterText) {
        doc.setFontSize(9);

        doc.setFont(
          "times",
          "normal"
        );

        doc.setTextColor(
          ...textRgbColor
        );

        doc.text(
          filterText,
          pageWidth / 2,
          yOffset,
          {
            align: "center",
          }
        );

        yOffset += 7;
      }

      // =====================================================
      // SUMMARY
      // =====================================================

      const summaryEntries = getSummaryEntries();
      if (summaryEntries.length) {
        doc.setFont("times", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...primaryColor);
        doc.text("Summary", margin, yOffset);
        yOffset += 5;

        autoTable(doc, {
          startY: yOffset,
          head: [["Item", "Value"]],
          body: summaryEntries.map(([key, value]) => [
            formatColumnName(String(key)),
            formatValue(value),
          ]),
          theme: "grid",
          margin: { left: margin, right: margin },
          tableWidth: 95,
          headStyles: { fillColor: primaryColor, textColor: hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText), fontSize: 8, fontStyle: "bold", font: "times" },
          bodyStyles: { textColor: textRgbColor, fontSize: 7, font: "times" },
        });
        yOffset = (doc.lastAutoTable?.finalY || yOffset) + 7;
      }

      // TABLE
      // =====================================================

      const pdfColumns = getPdfColumns();

      const tableColumns =
        pdfColumns.map(
          (column) =>
            formatColumnName(
              column
            )
        );

      const tableRows =
        reportData.map(
          (row) =>
            pdfColumns.map(
              (column) =>
                formatValue(
                  row[column]
                )
            )
        );

      autoTable(doc, {
        startY: yOffset,

        head: [tableColumns],

        body: tableRows,

        theme: "grid",

        margin: {
          top: 42,
          bottom: 14,
          left: margin,
          right: margin,
        },

        tableWidth: "auto",

        columnStyles: Object.fromEntries(
          pdfColumns.map((column, index) => {
            const width = getPdfColumnWidth(column);
            return [index, width !== "auto" ? { cellWidth: width } : {}];
          })
        ),

        headStyles: {
          fillColor:
            primaryColor,

          textColor:
            hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText),

          fontSize: 9,

          fontStyle: "bold",

          font: "times",

          cellPadding: 2,
        },

        bodyStyles: {
          fontSize: 7,

          font: "times",

          cellPadding: 2,

          textColor:
            textRgbColor,

          minCellHeight: 6,
        },

        didParseCell: (
          data
        ) => {
          data.cell.styles.valign =
            "middle";

          data.cell.styles.halign =
            "left";
        },

        // ===================================================
        // HEADER ON EVERY TABLE PAGE
        // ===================================================

        willDrawPage: () => {
          drawPdfHeader();
        },

        // ===================================================
        // FOOTER ON EVERY PAGE
        // ===================================================

        didDrawPage: () => {
          const currentPage =
            doc.internal.getNumberOfPages();

          doc.setFont(
            "times",
            "normal"
          );

          doc.setFontSize(8);

          doc.setTextColor(
            ...textRgbColor
          );

          doc.text(
            `Page ${currentPage}`,
            pageWidth -
              margin,
            pageHeight - 7,
            {
              align: "right",
            }
          );
        },
      });

      // =====================================================
      // FINAL PAGE NUMBER CORRECTION
      // =====================================================

      const totalPages =
        doc.internal.getNumberOfPages();

      for (
        let page = 1;
        page <= totalPages;
        page++
      ) {
        doc.setPage(page);

        doc.setFont(
          "times",
          "normal"
        );

        doc.setFontSize(8);

        doc.setTextColor(
          ...textRgbColor
        );

        doc.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 7,
          {
            align: "right",
          }
        );
      }

      // =====================================================
      // OPEN PDF
      // =====================================================

      window.open(
        doc.output("bloburl"),
        "_blank"
      );
    } catch (error) {
      console.error(
        "PDF generation error:",
        error
      );

      alert(
        "Unable to generate PDF. Please try again."
      );
    }
  };

  // =========================================================
  // FILTER MODAL
  // =========================================================

  const renderFilterModal = () => {
    return (
      <Modal
        open={showFilterModal}
        onClose={() =>
          setShowFilterModal(false)
        }
        BackdropProps={{
          sx: {
            backgroundColor:
              "rgba(15, 23, 42, 0.55)",
            backdropFilter:
              "blur(3px)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",
            width: {
              xs: "94%",
              sm: 620,
              md: 720,
            },
            maxHeight: "90vh",
            overflowY: "auto",

            bgcolor: (theme) =>
              theme.palette.mode ===
              "dark"
                ? "#111111"
                : "#ffffff",

            color: (theme) =>
              theme.palette.mode ===
              "dark"
                ? "#ffffff"
                : "#000000",

            borderRadius: 3,

            boxShadow: (theme) =>
              theme.palette.mode ===
              "dark"
                ? "0 20px 60px rgba(0,0,0,0.65)"
                : "0 20px 60px rgba(0,0,0,0.18)",

            outline: "none",

            "& .MuiInputBase-input":
              {
                color: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",
              },

            "& .MuiSelect-select":
              {
                color: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",
              },

            "& .MuiInputLabel-root":
              {
                color: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",
              },

            "& .MuiInputLabel-root.Mui-focused":
              {
                color: (theme) =>
                  theme.palette
                    .primary.main,
              },

            "& .MuiSelect-icon":
              {
                color: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",
              },

            "& .MuiOutlinedInput-notchedOutline":
              {
                borderColor: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#555555"
                    : "#d1d5db",
              },

            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                borderColor: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#888888"
                    : "#6b7280",
              },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: (theme) =>
                  theme.palette
                    .primary.main,
              },

            "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input":
              {
                WebkitTextFillColor:
                  (theme) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#9ca3af"
                      : "#374151",
              },

            "& .MuiSelect-select.Mui-disabled":
              {
                WebkitTextFillColor:
                  (theme) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#9ca3af"
                      : "#374151",
              },

            "& .MuiInputLabel-root.Mui-disabled":
              {
                color: (theme) =>
                  theme.palette
                    .mode ===
                  "dark"
                    ? "#9ca3af"
                    : "#6b7280",
              },

            "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
              {
                borderColor: (theme) =>
                  theme.palette.mode ===
                  "dark"
                    ? "#444444"
                    : "#d1d5db",
              },

            "&::-webkit-scrollbar":
              {
                width: 6,
              },

            "&::-webkit-scrollbar-track":
              {
                background:
                  "transparent",
              },

            "&::-webkit-scrollbar-thumb":
              {
                backgroundColor:
                  (theme) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#555555"
                      : "#b0b0b0",
                borderRadius: 10,
              },

            "&::-webkit-scrollbar-thumb:hover":
              {
                backgroundColor:
                  (theme) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#777777"
                      : "#888888",
              },
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              px: 3,
              py: 2.2,
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",

              borderBottom:
                (theme) =>
                  `1px solid ${
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#333333"
                      : "#e5e7eb"
                  }`,
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "#111111"
                  : "#ffffff",
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,

                color: (theme) =>
                  theme.palette
                    .mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",
              }}
            >
              Filters
            </Typography>

            <IconButton
              onClick={() =>
                setShowFilterModal(
                  false
                )
              }
              size="small"
              sx={{
                border:
                  (theme) =>
                    `1px solid ${
                      theme.palette
                        .mode ===
                      "dark"
                        ? "#444444"
                        : "#e5e7eb"
                    }`,

                borderRadius: 2,

                color: (theme) =>
                  theme.palette
                    .mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",

                "&:hover": {
                  backgroundColor:
                    (theme) =>
                      theme.palette
                        .mode ===
                      "dark"
                        ? "#222222"
                        : "#f3f4f6",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* FILTER BODY */}

          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },

                gap: 2,
              }}
            >
              {/* ORGANIZATION */}

              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Organization
                </InputLabel>

                <Select
                  name="organization"
                  value={
                    filters.organization
                  }
                  label="Organization"
                  disabled
                >
                  {organizations.length >
                  0 ? (
                    organizations.map(
                      (org) => (
                        <MenuItem
                          key={org.id}
                          value={org.id}
                        >
                          {org.name ||
                            org.organization_name ||
                            org.organization ||
                            `Organization ${org.id}`}
                        </MenuItem>
                      )
                    )
                  ) : (
                    <MenuItem
                      value={
                        filters.organization
                      }
                    >
                      {organizationName ||
                        `Organization ${filters.organization}`}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* BRANCH-WISE INFO */}

              {activeTab ===
                "branch" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems:
                      "center",
                    px: 1.5,
                    py: 1,

                    border:
                      (theme) =>
                        `1px solid ${
                          theme.palette
                            .mode ===
                          "dark"
                            ? "#444444"
                            : "#e5e7eb"
                        }`,

                    borderRadius: 2,

                    backgroundColor:
                      (theme) =>
                        theme.palette
                          .mode ===
                        "dark"
                          ? "#1a1a1a"
                          : "#f9fafb",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,

                      color: (
                        theme
                      ) =>
                        theme.palette
                          .mode ===
                        "dark"
                          ? "#d1d5db"
                          : "#374151",
                    }}
                  >
                    Branch-wise
                    report includes
                    all branches of
                    the selected
                    organization.
                  </Typography>
                </Box>
              )}

              {/* CATEGORY */}

              {activeTab ===
                "overall" && (
                <FormControl
                  fullWidth
                  size="small"
                  disabled={
                    !filters.branch
                  }
                >
                  <InputLabel>
                    Category
                  </InputLabel>

                  <Select
                    name="category"
                    value={
                      filters.category
                    }
                    label="Category"
                    onChange={
                      handleFilterChange
                    }
                  >
                    <MenuItem value="">
                      All Categories
                    </MenuItem>

                    {categories.map(
                      (
                        category
                      ) => (
                        <MenuItem
                          key={
                            category.id
                          }
                          value={
                            category.id
                          }
                        >
                          {category.name ||
                            category.category_name ||
                            category.category ||
                            `Category ${category.id}`}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              )}

              {/* STATUS */}

              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Status
                </InputLabel>

                <Select
                  name="status"
                  value={
                    filters.status
                  }
                  label="Status"
                  onChange={
                    handleFilterChange
                  }
                >
                  <MenuItem value="">
                    All Status
                  </MenuItem>

                  <MenuItem value="pending">
                    Pending
                  </MenuItem>

                  <MenuItem value="approved">
                    Approved
                  </MenuItem>

                  <MenuItem value="rejected">
                    Rejected
                  </MenuItem>
                </Select>
              </FormControl>

              {/* PAYMENT MODE */}

              {activeTab ===
                "overall" && (
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Payment Mode
                  </InputLabel>

                  <Select
                    name="payment_mode"
                    value={
                      filters.payment_mode
                    }
                    label="Payment Mode"
                    onChange={
                      handleFilterChange
                    }
                  >
                    <MenuItem value="">
                      All Payment Modes
                    </MenuItem>

                    <MenuItem value="cash">
                      Cash
                    </MenuItem>

                    <MenuItem value="bank">
                      Bank
                    </MenuItem>

                    <MenuItem value="upi">
                      UPI
                    </MenuItem>

                    <MenuItem value="card">
                      Card
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* ENTERED BY */}

              {activeTab ===
                "overall" && (
                <FormControl
                  fullWidth
                  size="small"
                  disabled={
                    !filters.branch ||
                    staff.length === 0
                  }
                >
                  <InputLabel>
                    Entered By
                  </InputLabel>

                  <Select
                    name="entered_by"
                    value={
                      filters.entered_by
                    }
                    label="Entered By"
                    onChange={
                      handleFilterChange
                    }
                  >
                    <MenuItem value="">
                      All Staff
                    </MenuItem>

                    {staff.map(
                      (
                        person,
                        index
                      ) => {
                        const staffId =
                          getStaffId(
                            person
                          );

                        return (
                          <MenuItem
                            key={
                              staffId ||
                              index
                            }
                            value={
                              staffId
                            }
                          >
                            {getStaffName(
                              person
                            )}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
              )}

              {/* APPROVED BY */}

              {activeTab ===
                "overall" && (
                <FormControl
                  fullWidth
                  size="small"
                  disabled={
                    !filters.branch ||
                    staff.length === 0
                  }
                >
                  <InputLabel>
                    Approved By
                  </InputLabel>

                  <Select
                    name="approved_by"
                    value={
                      filters.approved_by
                    }
                    label="Approved By"
                    onChange={
                      handleFilterChange
                    }
                  >
                    <MenuItem value="">
                      All Staff
                    </MenuItem>

                    {staff.map(
                      (
                        person,
                        index
                      ) => {
                        const staffId =
                          getStaffId(
                            person
                          );

                        return (
                          <MenuItem
                            key={
                              staffId ||
                              index
                            }
                            value={
                              staffId
                            }
                          >
                            {getStaffName(
                              person
                            )}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
              )}

              {/* DATE FROM */}

              {activeTab !==
                "monthly" && (
                <TextField
                  fullWidth
                  size="small"
                  label="Date From"
                  type="date"
                  name="date_from"
                  value={
                    filters.date_from
                  }
                  onChange={
                    handleFilterChange
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}

              {/* DATE TO */}

              {activeTab !==
                "monthly" && (
                <TextField
                  fullWidth
                  size="small"
                  label="Date To"
                  type="date"
                  name="date_to"
                  value={
                    filters.date_to
                  }
                  onChange={
                    handleFilterChange
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}

              {/* YEAR */}

              {activeTab ===
                "monthly" && (
                <TextField
                  fullWidth
                  size="small"
                  label="Year"
                  type="number"
                  name="year"
                  value={
                    filters.year
                  }
                  onChange={
                    handleFilterChange
                  }
                />
              )}
            </Box>
          </Box>

          <Divider
            sx={{
              borderColor:
                (theme) =>
                  theme.palette
                    .mode ===
                  "dark"
                    ? "#333333"
                    : "#e5e7eb",
            }}
          />

          {/* FOOTER */}

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 1,
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "#111111"
                  : "#ffffff",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              variant="text"
              startIcon={
                <RestartAltIcon />
              }
              onClick={
                handleReset
              }
              sx={{
                textTransform:
                  "none",

                color: (theme) =>
                  theme.palette
                    .mode ===
                  "dark"
                    ? "#ffffff"
                    : "#000000",

                fontWeight: 600,

                "&:hover": {
                  backgroundColor:
                    (theme) =>
                      theme.palette
                        .mode ===
                      "dark"
                        ? "#222222"
                        : "#f3f4f6",
                },
              }}
            >
              Reset
            </Button>

            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() =>
                  setShowFilterModal(
                    false
                  )
                }
                sx={{
                  textTransform:
                    "none",
                  borderRadius: 2,

                  color: (
                    theme
                  ) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#ffffff"
                      : "#000000",

                  borderColor: (
                    theme
                  ) =>
                    theme.palette
                      .mode ===
                    "dark"
                      ? "#555555"
                      : "#d1d5db",

                  "&:hover": {
                    borderColor:
                      (
                        theme
                      ) =>
                        theme.palette
                          .mode ===
                        "dark"
                          ? "#888888"
                          : "#6b7280",

                    backgroundColor:
                      (
                        theme
                      ) =>
                        theme.palette
                          .mode ===
                        "dark"
                          ? "#222222"
                          : "#f9fafb",
                  },
                }}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={() => {
                  fetchReport();
                  setShowFilterModal(
                    false
                  );
                }}
                disabled={loading}
                sx={{
                  textTransform:
                    "none",
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,

                  backgroundColor:
                    (theme) =>
                      theme.palette
                        .primary
                        .main,

                  color: (theme) =>
                    theme.palette
                      .primary
                      .contrastText,

                  "&:hover": {
                    backgroundColor:
                      (theme) =>
                        theme.palette
                          .primary
                          .dark,
                  },

                  "&.Mui-disabled":
                    {
                      backgroundColor:
                        (theme) =>
                          theme.palette
                            .action
                            .disabledBackground,

                      color: (theme) =>
                        theme.palette
                          .action
                          .disabled,
                    },
                }}
              >
                {loading
                  ? "Applying..."
                  : "Apply Filters"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  };

  // =========================================================
  // TABLE
  // =========================================================

  const renderTable = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
          }}
        >
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!reportData.length) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
          }}
        >
          <Typography color="text.primary">
            No expense report
            data found.
          </Typography>
        </Box>
      );
    }

    const filteredRows = reportData;
    const paginatedRows = filteredRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box>
        <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 2,
          border: (theme) =>
            `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: 900,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  backgroundColor: (theme) =>
                    theme.palette.primary.main,
                  color: (theme) =>
                    theme.palette.primary.contrastText,
                  borderBottom: "none",
                  py: 1.8,
                  px: 2,
                }}
              >
                S.No
              </TableCell>
              {columns.map(
                (column) => (
                  <TableCell
                    key={column}
                    sx={{
                      fontWeight: 700,
                      fontSize:
                        "0.85rem",
                      whiteSpace:
                        "nowrap",

                      backgroundColor:
                        (theme) =>
                          theme.palette
                            .primary
                            .main,

                      color: (theme) =>
                        theme.palette
                          .primary
                          .contrastText,

                      borderBottom:
                        "none",

                      py: 1.8,
                      px: 2,

                      "&.MuiTableCell-stickyHeader":
                        {
                          backgroundColor:
                            (
                              theme
                            ) =>
                              theme
                                .palette
                                .primary
                                .main,

                          color: (
                            theme
                          ) =>
                            theme
                              .palette
                              .primary
                              .contrastText,
                        },
                    }}
                  >
                    {formatColumnName(
                      column
                    )}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map(
              (row, index) => (
                <TableRow
                  key={
                    row.id ||
                    index
                  }
                  hover
                  sx={{
                    transition:
                      "all 0.2s ease",

                    "&:nth-of-type(even)":
                      {
                        backgroundColor:
                          (
                            theme
                          ) =>
                            theme
                              .palette
                              .action
                              .hover,
                      },

                    "&:hover": {
                      backgroundColor:
                        (
                          theme
                        ) =>
                          theme
                            .palette
                            .action
                            .selected,
                    },

                    "&:last-child td":
                      {
                        borderBottom: 0,
                      },
                  }}
                >
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      fontSize: "0.875rem",
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      borderColor: (theme) =>
                        theme.palette.divider,
                    }}
                  >
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  {columns.map(
                    (column) => (
                      <TableCell
                        key={column}
                        sx={{
                          py: 1.5,
                          px: 2,

                          fontSize:
                            "0.875rem",

                          color: (
                            theme
                          ) =>
                            theme
                              .palette
                              .text
                              .primary,

                          whiteSpace:
                            "nowrap",

                          borderColor:
                            (
                              theme
                            ) =>
                              theme
                                .palette
                                .divider,
                        }}
                      >
                        {formatValue(
                          row[
                            column
                          ]
                        )}
                      </TableCell>
                    )
                  )}
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(event, newPage) => {
            setPage(newPage);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{
            borderTop: (theme) =>
              `1px solid ${theme.palette.divider}`,
            mt: 1,
          }}
        />
      </Box>
    );
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
        }}
      >
          {/* =================================================
              TABS
          ================================================== */}
<Tabs
  value={activeTab}
  onChange={(_event, value) => {
    setActiveTab(value);
    setReportData([]);
    setPage(0);
    setError("");
  }}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  sx={{
    minHeight: 42,

    borderBottom: "1px solid",
    borderColor: "divider",

    "& .MuiTabs-indicator": {
      display: "none",
    },

    "& .MuiTab-root": {
      minHeight: 42,
      minWidth: {
        xs: 120,
        sm: 145,
      },
      px: 2,
      borderRadius: 1.5,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.875rem",
      color: "text.primary",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "action.hover",
        color: "text.primary",
      },
    },

    "& .MuiTab-root.Mui-selected": {
      backgroundColor: "primary.main",
      color: "primary.contrastText",

      "&:hover": {
        backgroundColor: "primary.dark",
      },
    },

    "& .MuiTabs-scrollButtons": {
      color: "primary.main",
    },
  }}
>
  {TAB_CONFIG.map((tab) => (
    <Tab
      key={tab.key}
      value={tab.key}
      label={tab.label}
    />
  ))}
</Tabs> 
          {/* =================================================
              FILTER TOOLBAR
          ================================================== */}

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "flex-end",
              alignItems: "center",
              gap: 1,
              mt: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            

            {/* FILTER BUTTON */}

            <Button
              variant="contained"
              startIcon={
                <FilterAltOutlinedIcon />
              }
              onClick={() =>
                setShowFilterModal(
                  true
                )
              }
              sx={{
                textTransform:
                  "none",
                borderRadius: 2,
                px: 2,
                fontWeight: 600,

                backgroundColor:
                  (theme) =>
                    theme.palette
                      .primary
                      .main,

                color: (theme) =>
                  theme.palette
                    .primary
                    .contrastText,

                "&:hover": {
                  backgroundColor:
                    (theme) =>
                      theme.palette
                        .primary
                        .dark,
                },
              }}
            >
              Filters
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                const rows = await fetchReport();
                if (rows.length) {
                  setTempPdfThemeColor(
                    pdfThemeColor || DEFAULT_PDF_COLORS.primary
                  );
                  setTempPdfTextColor(
                    pdfTextColor || DEFAULT_PDF_COLORS.bodyText
                  );
                  setShowColorPickerModal(true);
                }
              }}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                backgroundColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.contrastText,
                "&:hover": { backgroundColor: (theme) => theme.palette.primary.dark },
              }}
            >
              Generate Report
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={exportExcel}
              disabled={loading || !reportData.length}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Export Excel
            </Button>
          </Box>

          {/* =================================================
              TABLE
          ================================================== */}

          {renderTable()}
        </Box>
     

      {/* =====================================================
          PDF COLOR MODAL
      ====================================================== */}

      <ReportPdfColorModal
        open={showColorPickerModal}
        onClose={() => setShowColorPickerModal(false)}
        themeColor={tempPdfThemeColor}
        setThemeColor={setTempPdfThemeColor}
        textColor={tempPdfTextColor}
        setTextColor={setTempPdfTextColor}
        reportTitle={
          TAB_CONFIG.find((tab) => tab.key === activeTab)?.label ||
          "Expense Report"
        }
        onGenerate={async (themeColor, textColor) => {
          updatePdfThemeColor(themeColor);
          updatePdfTextColor(textColor);
          setShowColorPickerModal(false);
          await generatePDF(themeColor, textColor);
        }}
      />

      {/* =====================================================
          FILTER MODAL
      ====================================================== */}

      {renderFilterModal()}
    </Box>
  );
}

export default ExpenseReport;